import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { LineChart } from 'react-native-chart-kit';

import { EmptyState, Text } from '~/components/core';
import { useStyles } from '~/hooks';
import i18n from '~/i18n';
import { FONT_SIZE_S } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { buildCumulative, buildTradeRows, StrategyResultView, withCurrency } from '../../utils/results';

// react-native-chart-kit ships class-component types that aren't valid JSX element types under
// the project's React typings (same TS2786 as BasicLineChart); cast keeps the file type-clean.
const ProfitLineChart = LineChart as unknown as React.ComponentType<any>;

type Props = {
    results: StrategyResultView;
};

// Cumulative profit/loss over the backtest's trades.
export default function ChartTab({ results }: Props) {
    const styles = useStyles(stylesGenerator);
    const dimensions = useWindowDimensions();
    // Measured container width — the desktop pane is much narrower than the window, so
    // sizing from useWindowDimensions alone would overflow the pane.
    const [containerWidth, setContainerWidth] = useState(0);

    const cumulative = useMemo(
        () => buildCumulative(buildTradeRows(results.response, results.accountBalances)),
        [results]
    );

    // The curve plots account currency whenever the frozen rate was usable, so name the unit
    // rather than leave the axis ambiguous.
    const caption = withCurrency(
        i18n.t('ai-strategy-chart-caption'),
        results.accountBalances ? results.accountCurrency : results.instrumentCurrency
    );

    if (cumulative.length === 0) {
        return <EmptyState title="ai-strategy-no-trades" />;
    }

    const lineColor = styles.chartLineColor.color as string;
    const labelColor = styles.chartLabelColor.color as string;
    const gridColor = styles.chartGridColor.color as string;
    const chartWidth = Math.min(containerWidth || dimensions.width, scaleSize(720)) - scaleSize(28);
    const labelStep = Math.max(1, Math.ceil(cumulative.length / 7));
    const chartLabels = cumulative.map((_, i) => (i % labelStep === 0 ? String(i) : ''));

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.content}
            onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
        >
            <View style={styles.card}>
                <ProfitLineChart
                    transparent
                    fromZero
                    data={{
                        labels: chartLabels,
                        datasets: [{ data: cumulative, color: () => lineColor }],
                    }}
                    width={chartWidth}
                    height={scaleSize(220)}
                    segments={4}
                    withDots={false}
                    withShadow={false}
                    chartConfig={{
                        decimalPlaces: 0,
                        color: () => lineColor,
                        labelColor: () => labelColor,
                        fillShadowGradientOpacity: 0,
                        propsForBackgroundLines: { stroke: gridColor, strokeOpacity: 0.2 },
                    }}
                    style={styles.chart}
                />
                <Text noTranslate style={styles.caption}>
                    {caption}
                </Text>
            </View>
        </ScrollView>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: { flex: 1 },
        content: { padding: scaleSize(12) },
        card: {
            backgroundColor: theme.backgroundSecondary,
            borderRadius: scaleSize(8),
            padding: scaleSize(8),
        },
        chart: {
            marginVertical: scaleSize(4),
            borderRadius: scaleSize(6),
        },
        caption: {
            color: theme.text,
            fontSize: FONT_SIZE_S,
            textAlign: 'center',
            marginTop: scaleSize(4),
            marginBottom: scaleSize(4),
        },
        // Color holders read by the chart config (chart-kit needs raw color strings/fns).
        chartLineColor: { color: theme.text_upPrice ?? theme.primary },
        chartLabelColor: { color: theme.text },
        chartGridColor: { color: theme.textSecondary },
    });
